import React, {PureComponent, useState, useCallback, useEffect} from 'react';
import {NotificationManager} from 'react-notifications';
import FileSaver from 'filesaver.js';
import {DATE_RANGE_OPTIONS, MILLIS_PER_HOUR} from '../lib/timeline';
import * as api from '../lib/api';
import SideModal from './SideModal';
import Form from './Form';
import Button from './Button';
import DateTimeInput from './DateTimeInput';
import Tabs from './Tabs';
import {withUser} from '../lib/user';
import Input from './Input';
import { T } from '../components/I18n';
import styled from 'styled-components';

class ExportFormDataModal extends PureComponent {
  state = {
    tab: "Export",
    exportData: {
      startDate: null,
      endDate: null,      
    },
    schedule: {
      jobId: null,
      interval: "NEVER",
      timeInterval: "",
      scheduleTime: null
    },
    exporting: false,
    includeDeletedAnswers: false,
  }

  async componentDidUpdate(prevProps, prevState) {
    const {form, credentials, includeDeletedAnswers} = this.props;
    if(form && (!prevProps.form || (prevProps.form && prevProps.form.id !== form.id))) {
      this.setState({includeDeletedAnswers: !!includeDeletedAnswers});
      const schedules = await api.getScheduledTemplateExports(form, credentials);
      const schedule = schedules[0] || {
        jobId: null,
        interval: "NEVER",
        timeInterval: "",
        scheduleTime: null
      }
      this.setState({schedule});
    }
  }

  closeModal() {
    const {onClose} = this.props;
    onClose();
    this.setState({
      tab:"Export",
      includeDeletedAnswers: false,
      schedule: {
        jobId: null,
        interval: "NEVER",
        timeInterval: "",
        scheduleTime: null
    }
    })
  }

  renderTabContent() {
    const {tab, exportData, schedule, includeDeletedAnswers} = this.state;
    switch (tab) {
      case "Export":
        return (
            <ExportDataTab exportData={exportData} onChange={exportData => this.setState({exportData})} includeDeletedAnswers={includeDeletedAnswers} toggleDeleteExport={() => this.toggleDeleteExport()}/>
        );
      case "Schedule":
        return (<ScheduleExportDataTab schedule={schedule} onChange={schedule => this.setState({schedule})}/>);
      default:
        throw new Error("unknown Tab");
    }
  }

  renderTabs() {
    const {tab} = this.state;
    return (
      <Tabs>
        <Tabs.Tab onClick={() => this.setState({tab:"Export"})} active={tab === "Export"}>Export</Tabs.Tab>
        <Tabs.Tab onClick={() => this.setState({tab:"Schedule"})} active={tab === "Schedule"}>Schedule</Tabs.Tab>
      </Tabs>
    )
  }

  renderOptions() {
    switch(this.state.tab) {
      case "Export": 
        return [
          <Button onClick={() => this.handleExportData()} disabled={!this.canSubmit()}>Export</Button>
        ];
      case "Schedule":
        return [
          <Button onClick={() => this.handleSaveSchedule()} disabled={!this.canSubmit()}>Save</Button>
        ];
      default:
        return [];
    }
  }

  canSubmit() {
    const {tab, exporting, exportData, schedule} = this.state;
    if (exporting) {
      return false;
    }

    switch(tab) {
      case "Export": 
        return exportData.startDate && exportData.endDate;
      case "Schedule":
        if (schedule.interval === "NEVER" && schedule.id) {
          return true;
        }

        return schedule.interval && schedule.scheduleTime && schedule.timeInterval;
      default:
        return false;
    }
  }

  handleExportData = async () => {
    const {form, credentials} = this.props;
    let {exportData: {startDate, endDate, range}, includeDeletedAnswers} = this.state;

   if (range && range > 0) {
     const now = new Date();
      startDate = new Date(now.getTime() - range);
     endDate = now;
    }

   this.setState({exporting: true});
   try {
     const content = await api.exportSingleTemplateData(form.id, startDate, endDate, includeDeletedAnswers, credentials);
     this.setState({startDate: null, endDate: null});
     this.closeModal();
     setTimeout(() => FileSaver.saveAs(content, `${form.name}-${Date.now()}.xlsx`), 250);
   }
   catch (e) {
      if(e.status === 400) {
       NotificationManager.error('Too much data to export. Reduce your date range and try again.');
     }
      else {
       NotificationManager.error('Unable to export form data. Please try again.');
     }
    }
    finally {
      this.setState({exporting: false});
    }
  }

  handleSaveSchedule = async () => {
    if (this.state.schedule.interval === "NEVER") {
      await this.deleteSchedule();
    }
    else {
      await this.saveSchedule();
    }
  }

  saveSchedule = async () => {
    const {form, credentials} = this.props;
    const {schedule} = this.state;

    this.setState({exporting: true});

    try {
      const updatedSchedule = await api.saveTemplateExportSchedule(form, schedule, credentials);
      this.setState({schedule: updatedSchedule, exporting: false});
      NotificationManager.success(`Your schedule has been ${schedule.jobId ? "updated" : "created"}.`)
    }
    catch(e) {
      NotificationManager.error('Could not schedule this export.');
      this.setState({exporting: false});
    }
  }

  deleteSchedule = async () => {
    const {form, credentials} = this.props;
    const {schedule} = this.state;
    this.setState({exporting: true});

    try {
      await api.deleteTemplateExportSchedule(form, schedule, credentials);
      NotificationManager.success('Your schedule has been deleted.');
      this.setState({schedule: {
        jobId: null,
        interval: "NEVER",
        timeInterval: "",
        scheduleTime: null
      }, exporting: false});
    }
    catch (e) {
      NotificationManager.error('Could not delete the scheduled export');
      this.setState({exporting: false});
    }
  }

  toggleDeleteExport = () => {
    this.setState(prevState => ({includeDeletedAnswers: !prevState.includeDeletedAnswers}));
  }

  render() {
    const {form} = this.props;
    if (!form) {
      return null;
    }

    return (
      <SideModal
        title={form.name}
        open={form}
        onClose={() => this.closeModal()}
        tabs={this.renderTabs()}
        options={this.renderOptions()}>
          <SideModal.Content>
            {this.renderTabContent()}
          </SideModal.Content>
      </SideModal>
    );
  }
}

function getInitialExportRange(startDate, endDate) {
  if (!startDate || !endDate) {

    return DATE_RANGE_OPTIONS[0].range;
  }
  const diff = endDate.getTime() - startDate.getTime();
  const match = DATE_RANGE_OPTIONS.find(option => option.range > 0 && Math.abs(option.range - diff) < MILLIS_PER_HOUR);
  return match ? match.range : -1;
}

function ExportDataTab({exportData, onChange, includeDeletedAnswers, toggleDeleteExport}) {
  const {startDate, endDate} = exportData;
  const [selectedRange, setSelectedRange] = useState(() => getInitialExportRange(startDate, endDate));

  useEffect(() => {
    if (!startDate && !endDate) {
      const now = new Date();
      onChange({startDate: new Date(now.getTime() - DATE_RANGE_OPTIONS[0].range), endDate: now, range: DATE_RANGE_OPTIONS[0].range});
    }
  }, []);

  const handleRangeChange = useCallback((e) => {
    const range = Number(e.target.value);
    setSelectedRange(range);
    if (range > 0) {
      const now = new Date();
      onChange({startDate: new Date(now.getTime() - range), endDate: now, range});
    } else {
      onChange({startDate: null, endDate: null, range: null});
    }
  }, [onChange]);



  return (
    <>
      <Form.Group>
        <CheckboxOption>
          <input
            type="checkbox"
            checked={includeDeletedAnswers}
            onChange={() => toggleDeleteExport()}
          />
          <span>Include answers from deleted questions</span>
        </CheckboxOption>
        <HelperText>Deleted question data is excluded by default</HelperText>
      </Form.Group>
      <Form.Group>
        <Input
          id="date_range"
          type="select"
          label="Date Range"
          value={selectedRange}
          onChange={handleRangeChange}
        >
          {DATE_RANGE_OPTIONS.map(option => (
            <option key={option.range} value={option.range}>{option.label}</option>
          ))}
        </Input>
      </Form.Group>
      {selectedRange === -1 && (
        <>
          <Form.Group>
            <DateTimeInput
              label="From"
              placeholder="Choose a date &amp; time"
              date={startDate}
              onChange={startDate => onChange({startDate, endDate})}
            />
          </Form.Group>
          <Form.Group>
            <DateTimeInput
              label="To"
              placeholder="Choose a date &amp; time"
              date={endDate}
              onChange={endDate => onChange({startDate, endDate})}
            />
          </Form.Group>
        </>
      )}
    </>
  )
}

function ScheduleExportDataTab({schedule, onChange}) {
  const {jobId, interval, timeInterval, scheduleTime} = schedule;
  let dayString = "days";
  if (timeInterval && timeInterval === '1') {
    dayString = "day";
  }
  return (
    <>
    <Form.Group>
      <InfoText>Scheduled exports do not include deleted question data</InfoText>
    </Form.Group>
    <Form.Group>
      <Input 
        id="frequency"
        type="select"
        label="Export frequency"
        value={interval}
        onChange={e => onChange({jobId, interval: e.target.value, timeInterval, scheduleTime})}
        >
          <option value="NEVER">Never</option>
          <option value="DAY">Day</option>
          <option value="WEEK">Week</option>
          <option value="MONTH">Month</option>
      </Input>
    </Form.Group>
    {interval !== "NEVER" &&
      <>
        <Form.Group>
          <DateTimeInput
            label="Start Date"
            placeholder="Choose a date &amp; time"
            date={scheduleTime ? new Date(scheduleTime) : scheduleTime}
            onChange={scheduleTime => onChange({jobId, interval, timeInterval, scheduleTime})}
            helpText="When do you want to receive your first scheduled export?"
          />
        </Form.Group>
        <Form.Group>
          <Input
            id="timeInterval"
            label="Export data from"
            placeholder="Choose the data you want"
            type="number"
            value={timeInterval}
            helpText={<T isolate>Your scheduled export will contain data from the previous<T variable="daysCount"> {timeInterval === "" ? "N" : timeInterval}</T> {dayString}</T>}
            onChange={e => onChange({jobId, interval, timeInterval: e.target.value >= 0 ? e.target.value : 0, scheduleTime})}
            />
        </Form.Group>
      </>
    }
    </>
  )
}

const InfoText = styled.p`
  margin-bottom: 1rem;
  font-size: 16px;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 16px;

  input {
    width: 1rem;
    height: 1rem;
    margin: 0;
    cursor: pointer;
    accent-color: #0049ee;
    flex-shrink: 0;
  }
`;

const HelperText = styled.p`
  margin: 0.25rem 0 0 1.5rem;
  color: #747474;
  font-size: 0.875rem;
  line-height: 1.4;
`;

export default withUser(ExportFormDataModal);
